/**
 * v2's signed-in account control: who you are, settings, sign out.
 *
 * v2 had none of this. The landing page was a bare heading, so there was no
 * way to sign out or change a password without going to the v1 app, and no
 * way to tell which account you were in.
 *
 * Lives under views/v2 and imports nothing from v1's components. See
 * AccountSettingsV2 for why the duplication is deliberate.
 */

import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '../../store/auth';
import { AccountSettingsV2 } from './AccountSettingsV2';

function initials(name: string, email: string): string {
  const source = name.trim() || email.trim();
  if (!source) return '?';
  const parts = source.split(/[\s@._-]+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((p) => p[0]);
  return letters.join('').toUpperCase() || source[0].toUpperCase();
}

export function AccountMenuV2() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);

  // Close on an outside click or Escape. Without the keyboard half, the menu
  // can only be dismissed with a mouse.
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!user) return null;

  const itemCls =
    'block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50';

  return (
    <div className="relative shrink-0" ref={wrapper}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded border border-gray-300 py-1 pl-1 pr-2 text-sm text-gray-700 hover:bg-gray-50"
      >
        <span
          aria-hidden="true"
          className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white"
        >
          {initials(user.name, user.email)}
        </span>
        <span className="hidden max-w-[12rem] truncate sm:inline">
          {user.name || user.email}
        </span>
        <span aria-hidden="true" className="text-xs text-gray-400">
          &#9662;
        </span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1 w-60 overflow-hidden rounded border border-gray-200 bg-white shadow-lg"
        >
          <div className="border-b border-gray-100 px-3 py-2">
            <div className="truncate text-sm font-medium text-gray-900">
              {user.name || 'Signed in'}
            </div>
            <div className="truncate text-xs text-gray-500">{user.email}</div>
          </div>
          <button
            type="button"
            role="menuitem"
            className={itemCls}
            onClick={() => {
              setOpen(false);
              setShowSettings(true);
            }}
          >
            Account settings
          </button>
          <button
            type="button"
            role="menuitem"
            className={`${itemCls} border-t border-gray-100`}
            onClick={() => {
              setOpen(false);
              logout();
            }}
          >
            Sign out
          </button>
        </div>
      )}

      {showSettings && <AccountSettingsV2 onClose={() => setShowSettings(false)} />}
    </div>
  );
}
